/**
 * Tippy options shared by every tooltip in the app, so all popups look and
 * behave alike.
 *
 * - `aria`: tippy's ARIA wiring is off; each trigger already exposes the text
 *   to assistive technology, so the popup is a visual enhancement only.
 * - `interactive` + `appendTo`: the popup stays open while the pointer is over
 *   it (WCAG 1.4.13). It is appended to the trigger's modal `<dialog>` (the
 *   top layer hides anything outside it) or else to `<body>`: the default for
 *   an interactive popup is the trigger's parent, which a table cell with
 *   `overflow: hidden` would clip. Fixed positioning keeps a dialog's own
 *   `overflow: auto` from clipping it.
 * - Escape closes the popup while it is shown (WCAG 1.4.13). The key is
 *   handled in the capture phase and its default is prevented, so a dialog
 *   around the trigger stays open and closes on the next Escape.
 *
 * Call once per tippy instance: the Escape listener is tied to the returned
 * object. `onShow` in `overrides` runs first and may cancel the show; `onHide`
 * and `onDestroy` run after the built-in ones.
 *
 * @param {import('tippy.js').Props} overrides
 * @returns {import('tippy.js').Props}
 */
export function tooltipOptions(overrides = {}) {
  let closeOnEscape = null

  const removeListener = () => {
    if (closeOnEscape) document.removeEventListener('keydown', closeOnEscape, true)
    closeOnEscape = null
  }

  return {
    aria: { content: null, expanded: null },
    interactive: true,
    appendTo: (reference) => reference.closest('dialog') ?? document.body,
    popperOptions: { strategy: 'fixed' },
    ...overrides,
    onShow: (instance) => {
      if (overrides.onShow?.(instance) === false) return false
      removeListener()
      closeOnEscape = (event) => {
        if (event.key !== 'Escape') return
        event.preventDefault()
        instance.hide()
      }
      document.addEventListener('keydown', closeOnEscape, true)
    },
    onHide: (instance) => {
      removeListener()
      return overrides.onHide?.(instance)
    },
    onDestroy: (instance) => {
      removeListener()
      return overrides.onDestroy?.(instance)
    },
  }
}
